import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import DAMImageUtil from '../../global/dam-image-util';
import { default as languageStrings } from '../../global/l10n/GpnSnapshot.json';
import Util from '../../global/util';
import { OPNDetail } from '../opn-details/opn-details.interface';
import OPNDao, { OPNDaoConfig } from '../opn-snapshot/opn-snapshot.dao';
import { OPNParameters, Price } from '../opn-snapshot/opn-snapshot.interface';
import GPNDao from './gpn-snapshot.dao';
import { GPNMatch } from './gpn-snapshot.interface';

@Component({
    tag: 'ti-gpn-snapshot',
    styleUrl: 'gpn-snapshot.scss',
    shadow: true
})
export class GpnSnapshot {

    private _dataSheetLabel: string;
    private _dataSheetUrl: string;
    private _docSheetUrl: string;
    private _productPageUrl: string;
    private _viewAndOrderLabel: string;
    private _viewAllPartsLabel: string;
    private _snapshotUrl: string;
    private _statusUrl: string;
    private _commonQueryString: string = 'keyMatch={searchTerm}&tisearch={tisearch}&usecase={usecase}';

    /**
     * Load Statuses
     */
    private _statusType = {
        LOADING: 'loading',
        READY: 'ready',
        ERROR: 'error'
    }

    /**
     * The element for this component
     */
    @Element() _hostElement: HTMLElement;

    /**
     * Holds gpn response data
     *
     * @type {GPNMatch}
     */
    @State() gpnMatchDetails: GPNMatch;

    /**
     * Keep track of the status of the component so that we can render
     * a response to the user accordingly.
     *
     * @type {string}
     * @default _statusType.Loading
     */
    @State() status: string = this._statusType.LOADING;
    @Watch('status')
    handleComponentStatusUpdate(newValue: string, oldValue: string) {
        if (newValue !== oldValue && newValue === this._statusType.READY) {
            this.componentRendered.emit({
                tag: this._hostElement.tagName
            });
        }
    }

    @State() opnDetailList: OPNDetail[];

    /**
     * Value for tisearch metrics parameter
     *
     * @type {string}
     * @default 'universal_search'
     */
    @Prop() tisearch: string = 'universal_search';

    /**
     * Default image to be displayed if the image for the gpn is not found.
     * Should be a relative path from the server root.
     *
     * @type {string}
     * @default '/content/dam/ticom/images/icons/svg-icons/no-image-available-icon.svg'
     */
    @Prop() defaultImage: string = '/content/dam/ticom/images/icons/svg-icons/no-image-available-icon.svg';

    /**
     * GPN value
     *
     * @type {string}
     */
    @Prop() gpn: string;

    /**
     * URL for the image api
     *
     * @type {string}
     * @default '/imagemetadata/api'
     */
    @Prop() imageApiUrl: string = '/imagemetadata/api';

    /**
    * Value of locale
    *
    * @type {string}
    * @default 'en-US'
    */
    @Prop() locale: string = 'en-US';

    /**
     * Flag for setting whether or not the OPN list is expanded
     *
     * @type {boolean}
     * @default false
     */
    @Prop() opnListExpanded: boolean = false;

    /**
    * URL for the search api
    * @type {string}
    * @default '/search/gpn'
    */
    @Prop() searchApiUrl: string = '/search/gpn';

    /**
     * root URL for ti.com
     * @type {string}
     * @default 'https://www.ti.com'
     */
    @Prop() tiHost: string = 'https://www.ti.com';

    /**
     * Store host value for add-to-cart
     *
     * @type {string}
     * @default "https://www.ti.com"
     */
    @Prop() tiStoreHost: string = 'https://www.ti.com';

    /**
     * Store CN Host value for add-to-cart
     *
     * @type {string}
     * @default "https://www.ti.com.cn"
     */
    @Prop() tiStoreHostCn: string = 'https://www.ti.com.cn';

    /**
    * Catch link clicks and attach analytics query parameters
    * @param event
    */
    @Listen('click')
    clickHandler(event: any) {
        let tag = (event.composedPath && event.composedPath()[0]) || event.target as HTMLElement;
        // synopsis image click
        if (tag.className === 'ti-image-object') {
            tag = tag.parentElement.parentElement;
        }
        if (tag.tagName.toLowerCase() === 'a' && tag.getAttribute('href')) {
            event.preventDefault();
            event.stopPropagation(); //Fix to prevent multiple tabs from opening - Tarek - 1/24/2022
            const url = Util.formatUrl(
                tag.getAttribute('href'),
                {
                    searchTerm: this.gpn,
                    tisearch: this.tisearch.toLocaleLowerCase(),
                    usecase: this.gpnMatchDetails.hasAlternates ? 'GPN-ALT' : 'GPN'
                },
                this._commonQueryString
            );
            if (tag.getAttribute('target') === '_blank') {
                window.open(url, '_blank');
            } else {
                window.location.href = url;
            }
        }
    }
    /**
     * Method to set the gpn data
     */
    @Method()
    public async updateData(gpnMatch: GPNMatch) {
        this.opnDetailList = undefined;
        this.opnListExpanded = false;
        if (gpnMatch) {
            this.gpnMatchDetails = gpnMatch;
            this.gpn = gpnMatch.gpn;
            this.status = this._statusType.READY
        }
        else {
            this.status = this._statusType.ERROR;
        }
    }

    /**
     * Event for when component section has rendered
     */
    @Event() componentRendered: EventEmitter;

    componentWillLoad() {
        if (this.gpn) {
            this._updateData();
        }
        this._updateLanguageStrings();
    }

    private async _updateData() {
        this.updateData(await GPNDao.searchGPNMatch(this.gpn, this.locale, this.searchApiUrl, this.tiHost));
    }

    /**
     * Function to update language.
     */
    private _updateLanguageStrings() {
        this.locale = Util.getValidLocale(this.locale);
        this._dataSheetLabel = languageStrings[this.locale]['dataSheet'];
        this._dataSheetUrl = languageStrings[this.locale]['litUrl'];
        this._docSheetUrl = languageStrings[this.locale]['docUrl'];
        this._productPageUrl = languageStrings[this.locale]['productFolderUrl'];
        this._viewAndOrderLabel = languageStrings[this.locale]['viewAndOrderLabel'];
        this._viewAllPartsLabel = languageStrings[this.locale]['viewAllPartsLabel'];
        this._statusUrl = languageStrings[this.locale]['statusUrl'];
    }

    private async _getOPNDetailList(): Promise<OPNDetail[]> {
        const opnDetails: Array<OPNParameters> = await GPNDao.fetchOPNDetails(this.gpn, this.gpnMatchDetails.opns.slice(0, 5), this.locale);
        const opnConfig: OPNDaoConfig = new OPNDaoConfig();
        opnConfig.tiHost = GPNDao.tiHost || opnConfig.tiHost;
        const customReelPriceList: Price[] = await OPNDao.fetchCustomReelFees(opnConfig);
        const customReelFees: string = JSON.stringify(customReelPriceList.map(currencyData => {
            return {
                "currency": currencyData.currency,
                "currency_price": currencyData.currencyPrice ? currencyData.currencyPrice.toString() : ''
            }
        }));
        const currencyExchangeRateMap: { [currency: string]: number; } = await GPNDao.fetchCurrencyExchangeRate(this.gpn, this.locale);
        const opnDetailList = opnDetails.map((opnParam) => {
            return {
                opn: opnParam.orderablePartNumber,
                gpn: this.gpnMatchDetails.gpn,
                customReelPriceList: customReelFees,
                currencyExchangeRateMap: currencyExchangeRateMap,
                opnParameters: opnParam
            } as OPNDetail;
        });
        return opnDetailList;
    }


    private _handleViewAndOrderClick() {
        this.opnListExpanded = !this.opnListExpanded;
        const opnList: HTMLElement = this._hostElement.shadowRoot.querySelector('.ti-gpn-snapshot-opn-list');
        if (opnList) opnList.style.display = this.opnListExpanded ? '' : 'none';
    }

    async componentWillRender() {
        if (this.status !== this._statusType.READY) return;
        if (this.opnDetailList == undefined) {
            if (this.opnListExpanded && this.gpnMatchDetails) {
                this.opnDetailList = await this._getOPNDetailList();
            }
        }
        if (this.gpnMatchDetails && this.gpnMatchDetails.gpn) {
            this._snapshotUrl = await DAMImageUtil.getInstance(this.imageApiUrl).getFirstImageUrl(this.gpnMatchDetails.gpn, this.locale, 'gpn', 'IC PACKAGE IMAGE ASSET', 'singlesmall');
        }
        this._snapshotUrl = this._snapshotUrl || `${this.tiHost}${this.defaultImage}`;
    }

    render() {
        if (this.status !== this._statusType.READY) {
            return '';
        }
        const viewProductFamilyUrl = Util.formatUrl(
            this.gpnMatchDetails.familyUrl,
            {
                searchTerm: this.gpn,
                tisearch: this.tisearch.toLocaleLowerCase(),
                usecase: this.gpnMatchDetails.hasAlternates ? 'GPN-ALT' : 'GPN'
            },
            this._commonQueryString
        );
        return (
            <Host>
                <ti-card appearance="plain-white">
                    <div class="ti-gpn-snapshot">
                        <div class="ti-gpn-snapshot-fixed">
                            <ti-product-synopsis
                                product-url={`${this.tiHost}${this._productPageUrl}${this.gpnMatchDetails.gpn}`}
                                image-url={this._snapshotUrl}
                                default-image={`${this.tiHost}${this.defaultImage}`}>
                                {this.gpnMatchDetails.newFlag &&
                                    <a slot="new-badge" class="new-badge">{languageStrings[this.locale]["newBadgeLabel"]}</a>
                                }
                                <a slot="product-family" href={this.gpnMatchDetails.familyUrl} innerHTML={this.gpnMatchDetails.familyName}></a>
                                <a slot="product-link" href={`${this.tiHost}${this._productPageUrl}${this.gpnMatchDetails.gpn}`}>{this.gpnMatchDetails.gpn}</a>
                                <ti-product-status compact slot="product-status"
                                    status={this.gpnMatchDetails.statusId}
                                    tooltip={this.gpnMatchDetails.statusDescription}>
                                    <a data-navtitle="status-badge" href={`${this.tiHost}${this._statusUrl}`}>{this.gpnMatchDetails.status}</a>
                                </ti-product-status>
                                <a slot="description" href={`${this.tiHost}${this._productPageUrl}${this.gpnMatchDetails.gpn}`} innerHTML={this.gpnMatchDetails.description}></a>
                                <div slot="bottom" >
                                    {this.gpnMatchDetails.hasDatasheet &&
                                        <div class="ti-gpn-snapshot-data-sheet">
                                            <span>{this._dataSheetLabel}:</span>
                                            <a data-navtitle="data sheet-pdf" href={`${this.tiHost}${this._dataSheetUrl}${this.gpnMatchDetails.gpn}`} target="_blank">
                                                PDF
                                            </a>
                                            {this.gpnMatchDetails.hasHtmlDoc &&
                                                <div>
                                                    <span class="ti-gpn-snapshot-divider">|</span>
                                                    <a data-navtitle="data sheet-html" href={`${this.tiHost}${this._docSheetUrl}${this.gpnMatchDetails.gpn}/datasheet`} target="_blank">
                                                        HTML
                                                    </a>
                                                </div>
                                            }
                                        </div>
                                    }
                                    {this.gpnMatchDetails.hasAlternates &&
                                        <div class="ti-gpn-snapshot-view-alternates">
                                            <ti-selection-tool-view-alternates
                                                gpn={this.gpnMatchDetails.gpn}
                                                locale={this.locale}
                                                part-number={this.gpnMatchDetails.gpn}>
                                                <button class="ti_p-iconText">
                                                    <span class="ti_p-iconText-icon">
                                                        <ti-svg-icon appearance="secondary" size="s" icon-set="actions">open-in-new</ti-svg-icon>
                                                    </span>
                                                    {languageStrings[this.locale]['viewAlternatesLabel']}
                                                </button>
                                            </ti-selection-tool-view-alternates>
                                        </div>
                                    }
                                </div>
                            </ti-product-synopsis>
                        </div>
                        <div class="ti-gpn-snapshot-quicklinks">
                            {(this.gpnMatchDetails.opns && this.gpnMatchDetails.opns.length > 0) &&
                                <button data-navtitle="view and order parts" class={this.opnListExpanded ? 'ga-track ti-gpn-snapshot-opn-list-expander expanded' : 'ga-track ti-gpn-snapshot-opn-list-expander collapsed'}
                                    onClick={() => this._handleViewAndOrderClick()}>
                                    {this._viewAndOrderLabel}
                                    <ti-svg-icon appearance="secondary" size="s" icon-set="objects">chevron-up</ti-svg-icon>
                                </button>
                            }
                        </div>
                    </div>
                    <div class='ti-gpn-snapshot-opn-list' style={{ 'display': 'none' }}>
                        {(this.gpnMatchDetails.opns && this.gpnMatchDetails.opns.length > 0) &&
                            <div class='ti-gpn-snapshot-opn-list-content'>
                                {(this.opnDetailList && this.opnDetailList.length > 0) ?
                                    this.opnDetailList.map((data: OPNDetail) => {
                                        return (
                                            <ti-opn-details
                                                ti-host={this.tiHost}
                                                locale={this.locale}
                                                ti-store-host={this.tiStoreHost}
                                                ti-store-host-cn={this.tiStoreHostCn}
                                                opn-detail-data={JSON.stringify(data)}
                                                usecase='GPN'
                                                ti-metrics-location='ti.com-gpnsearch'
                                            >
                                            </ti-opn-details>
                                        )
                                    })
                                    :
                                    <div class="ti-gpn-snapshot-progress">
                                        <ti-progress></ti-progress>
                                    </div>
                                }
                                {this.opnDetailList &&
                                    <div class="ti-gpn-snapshot-opn-list-content-view-all">
                                        <div class="ti-gpn-snapshot-opn-list-content-view-all-product-family-link">
                                            {(this.gpnMatchDetails.opns.length > 5) &&
                                                <ti-button appearance="compact secondary">
                                                    <a data-navtitle="view all parts" href={`${this.tiHost}${this._productPageUrl}${this.gpnMatchDetails.gpn}#order-quality`}>{this._viewAllPartsLabel}</a>
                                                </ti-button>
                                            }
                                            <div>
                                                <a data-navtitle="view product family" href={viewProductFamilyUrl} class="ti-gpn-snapshot-product-family-link">
                                                    <ti-svg-icon size="s" appearance="secondary">parametric-filter</ti-svg-icon>
                                                    <span innerHTML={languageStrings[this.locale]['viewAllLabel'].replace('{0}', this.gpnMatchDetails.familyName)}></span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                </ti-card>
                <ti-oss-custom-reel-dialog locale={this.locale}></ti-oss-custom-reel-dialog>
                <ti-oss-sku-dialog locale={this.locale}></ti-oss-sku-dialog>
            </Host>
        )
    }
}
